import { useCallback, useRef, useState } from 'react';
import {
  useGetAlertsPagePayloadLazyQuery,
  useGetSavedSegmentsLazyQuery,
  useGetSessionsLazyQuery,
  useGetVisualizationsLazyQuery,
} from '@gonfalon/observability-graphql/hooks';
import { SavedSegmentEntityType } from '@gonfalon/observability-graphql/schemas';
import moment from 'moment';

import type { Resource, ResourceOrQuery } from '../../resources';

const TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.000000000Z';
const SEARCH_RESULT_COUNT = 10;

function buildDateRange() {
  return {
    start_date: moment().subtract(24, 'hours').format(TIME_FORMAT),
    end_date: moment().format(TIME_FORMAT),
  };
}

export function useResourceSearch(projectId: string) {
  const [results, setResults] = useState<ResourceOrQuery[]>([]);
  const [loading, setLoading] = useState(false);
  const searchIdRef = useRef(0);

  const [fetchSessions] = useGetSessionsLazyQuery({ fetchPolicy: 'network-only' });
  const [fetchVisualizations] = useGetVisualizationsLazyQuery({ fetchPolicy: 'network-only' });
  const [fetchAlerts] = useGetAlertsPagePayloadLazyQuery({ fetchPolicy: 'network-only' });
  const [fetchSavedSegments] = useGetSavedSegmentsLazyQuery({ fetchPolicy: 'network-only' });

  const search = useCallback(
    async (resourceType: Resource, query: string) => {
      const id = ++searchIdRef.current;
      setLoading(true);

      try {
        let mapped: ResourceOrQuery[] = [];

        if (resourceType === 'Sessions') {
          const { data } = await fetchSessions({
            variables: {
              project_id: projectId,
              count: SEARCH_RESULT_COUNT,
              params: { query, date_range: buildDateRange() },
              sort_desc: true,
            },
          });
          mapped =
            data?.sessions?.sessions?.map((session) => ({
              id: session.secure_id,
              payload: session,
              type: 'Sessions' as const,
            })) ?? [];
        } else if (resourceType === 'Alert') {
          const { data } = await fetchAlerts({
            variables: { project_id: projectId },
          });
          const lowerQuery = query.toLowerCase();
          mapped =
            data?.alerts
              ?.filter((alert): alert is NonNullable<typeof alert> => alert !== null && alert !== undefined)
              .filter((alert) => !lowerQuery || alert.name.toLowerCase().includes(lowerQuery))
              .slice(0, SEARCH_RESULT_COUNT)
              .map((alert) => ({
                id: String(alert.id),
                payload: { ...alert, alert_id: alert.id },
                type: 'Alert' as const,
              })) ?? [];
        } else if (resourceType === 'SavedSearch') {
          const entityTypes = [
            SavedSegmentEntityType.Error,
            SavedSegmentEntityType.Log,
            SavedSegmentEntityType.Session,
            SavedSegmentEntityType.Trace,
          ];
          const fetchResults = await Promise.all(
            entityTypes.map((entityType) =>
              fetchSavedSegments({
                variables: { project_id: projectId, entity_type: entityType },
              }).then(({ data }) => ({ data, entityType })),
            ),
          );
          const allSegments = fetchResults.flatMap(
            ({ data, entityType }) =>
              data?.saved_segments
                ?.filter((s): s is NonNullable<typeof s> => s !== null && s !== undefined)
                .map((segment) => ({
                  id: String(segment.id),
                  payload: { ...segment, saved_search_id: segment.id, entity_type: entityType },
                  type: 'SavedSearch' as const,
                })) ?? [],
          );
          const lowerQuery = query.toLowerCase();
          mapped = allSegments
            .filter((s) => !lowerQuery || s.payload.name.toLowerCase().includes(lowerQuery))
            .slice(0, SEARCH_RESULT_COUNT);
        } else if (resourceType === 'Dashboard') {
          const { data } = await fetchVisualizations({
            variables: {
              project_id: projectId,
              input: query,
              count: SEARCH_RESULT_COUNT,
              offset: 0,
            },
          });
          mapped =
            data?.visualizations?.results?.map((viz) => ({
              id: String(viz.id),
              payload: viz,
              type: 'Dashboard' as const,
            })) ?? [];
        }

        // Only apply if this is still the latest search
        if (id === searchIdRef.current) {
          setResults(mapped);
        }
      } finally {
        if (id === searchIdRef.current) {
          setLoading(false);
        }
      }
    },
    [projectId, fetchSessions, fetchVisualizations, fetchAlerts, fetchSavedSegments],
  );

  const clearResults = useCallback(() => {
    searchIdRef.current++;
    setResults([]);
    setLoading(false);
  }, []);

  return { results, loading, search, clearResults };
}
